import { useQuery } from '@tanstack/react-query';
import { fetchDashboardStats } from '../api/admin';

export function Dashboard() {
    const { data, isLoading, error } = useQuery({
        queryKey: ['dashboard-stats'],
        queryFn: fetchDashboardStats,
        refetchInterval: 30000, // Refetch every 30 seconds
    });

    if (isLoading) {
        return <div className="text-center py-8">Loading dashboard...</div>;
    }

    if (error) {
        return <div className="text-center py-8 text-red-500">Error loading dashboard</div>;
    }

    const stats = data || {};

    return (
        <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Dashboard
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <StatCard
                    title="Total Products"
                    value={stats.totalProducts || 0}
                    icon="📦"
                    color="bg-blue-500"
                />
                <StatCard
                    title="Total Licenses"
                    value={stats.totalLicenses || 0}
                    icon="🎫"
                    color="bg-green-500"
                />
                <StatCard
                    title="Active Servers"
                    value={stats.activeServers || 0}
                    icon="🖥️"
                    color="bg-purple-500"
                />
                <StatCard
                    title="Expiring Soon"
                    value={stats.expiringLicenses || 0}
                    icon="⚠️"
                    color="bg-yellow-500"
                />
            </div>

            {stats.expiringLicensesList && stats.expiringLicensesList.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Licenses Expiring Soon
                    </h2>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead>
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        License Key
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Expires At
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                        Server Count
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {stats.expiringLicensesList.map((license: any) => (
                                    <tr key={license.id}>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">
                                            {license.licenseKey}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">
                                            {license.expiresAt ? new Date(license.expiresAt).toLocaleDateString() : 'Never'}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">
                                            {license.activatedServers?.length || 0}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

interface StatCardProps {
    title: string;
    value: number;
    icon: string;
    color: string;
}

function StatCard({ title, value, icon, color }: StatCardProps) {
    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center">
                <div className={`text-3xl ${color} w-12 h-12 rounded-full flex items-center justify-center`}>
                    {icon}
                </div>
                <div className="ml-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">{title}</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
                </div>
            </div>
        </div>
    );
}